import { useRef, useState } from "react";
import type { PersonKurz } from "../personen";
import { anzeigename } from "../namen";
import { Avatar } from "./Avatar";
import { Schwebend } from "./Schwebend";
import { Stapel } from "./Stapel";
import s from "./auswahl.module.css";

interface Props {
  /** IDs der Verantwortlichen, die erste zuerst */
  ausgewaehlt: readonly string[];
  personen: readonly PersonKurz[] | undefined;
  /** Wen die Liste anbietet. Ohne Vorstandsrecht nur einen selbst. */
  waehlbar: readonly PersonKurz[];
  onHinzu: (id: string) => void;
  onWeg: (id: string) => void;
  /** Darf diese Person entfernt werden? Ohne Vorstandsrecht nur man selbst. */
  entfernbar?: ((id: string) => boolean) | undefined;
  /** chips: Chips und „+ Person“ (Dialog). stapel: ein Knopf mit dem Stapel (Zeilen). */
  art?: "chips" | "stapel" | undefined;
  gesperrt?: boolean | undefined;
  /** Wofür, für Screenreader: „Verantwortlich für „Titel““ */
  label: string;
}

/** Personen auswählen (AUFGABEN-STUFE2.md, Abschnitt 3.4). Jedes Häkchen speichert sofort, mehrere sind möglich. */
export function PersonenAuswahl({ ausgewaehlt, personen, waehlbar, onHinzu, onWeg, entfernbar = () => true, art = "chips", gesperrt, label }: Props) {
  const knopf = useRef<HTMLButtonElement>(null);
  const [offen, setOffen] = useState(false);
  const gewaehlt = ausgewaehlt.map((id) => personen?.find((p) => p.id === id)).filter((p): p is PersonKurz => !!p);
  const liste = [...waehlbar, ...gewaehlt.filter((p) => !waehlbar.some((w) => w.id === p.id))];

  const auswahl = (
    <Schwebend anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} label={label} breite={320}>
      <ul className={s.personen}>
        {liste.map((p) => {
          const an = ausgewaehlt.includes(p.id);
          const darf = an ? entfernbar(p.id) : waehlbar.some((w) => w.id === p.id);
          return (
            <li key={p.id}>
              <label className={[s.personZeile, !darf && s.personGesperrt].filter(Boolean).join(" ")}>
                <input type="checkbox" checked={an} disabled={!darf} onChange={() => (an ? onWeg(p.id) : onHinzu(p.id))} />
                <Avatar person={p} groesse={30} />
                <span className={s.personName}>{p.vorname || anzeigename(p)}</span>
                <span className={s.meta}>{p.vorstand ? "Vorstand" : "Mitglied"}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <div className={s.personenFuss}>
        <span className={s.meta}>Mehrere möglich</span>
        <button
          type="button"
          className={s.fertig}
          onClick={() => {
            setOffen(false);
            knopf.current?.focus();
          }}
        >
          Fertig
        </button>
      </div>
    </Schwebend>
  );

  if (art === "stapel") {
    const namen = gewaehlt.map((p) => p.vorname || anzeigename(p)).join(", ") || "Offen";
    return (
      <span className={s.kalenderFeld}>
        <button
          ref={knopf}
          type="button"
          className={s.stapelKnopf}
          aria-haspopup="dialog"
          aria-expanded={offen}
          aria-label={`${label}: ${namen}. Ändern`}
          disabled={gesperrt}
          onClick={() => setOffen(!offen)}
        >
          <Stapel personen={gewaehlt} />
        </button>
        {auswahl}
      </span>
    );
  }

  return (
    <span className={s.chips} role="group" aria-label={label}>
      {gewaehlt.map((p) => (
        <span key={p.id} className={s.chip}>
          <Avatar person={p} groesse={26} />
          {p.vorname || anzeigename(p)}
          {!gesperrt && entfernbar(p.id) ? (
            <button type="button" className={s.chipWeg} aria-label={`${p.vorname || anzeigename(p)} entfernen`} onClick={() => onWeg(p.id)}>
              ✕
            </button>
          ) : null}
        </span>
      ))}
      {gewaehlt.length === 0 ? <span className={s.meta}>Offen, noch niemand</span> : null}
      {!gesperrt && waehlbar.length > 0 ? (
        <button ref={knopf} type="button" className={s.plusPerson} aria-haspopup="dialog" aria-expanded={offen} onClick={() => setOffen(!offen)}>
          + Person
        </button>
      ) : null}
      {auswahl}
    </span>
  );
}
